import { components } from 'api/goodtrust/swagger'
import { deleteUserDocument, putUserDocumentContact } from 'api/goodtrust/user/document'
import { Button } from 'components/button/Button'
import { ContactShareItem } from 'components/contact/contactShareForm/ContactShareForm'
import {
  ContactShareInvite,
  parseSharedInvitedContacts,
} from 'components/contact/contactShareInvite/ContactShareInvite'
import { Icon } from 'components/icon'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useModal } from 'components/modal/Modal'
import { usePopperModal } from 'components/modal/PopperModal'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import {
  useCardStatusComplete,
  useCardStatusMissingTC,
  WizardGridCard,
} from 'components/wizard/wizardGridCard/WizardGridCard'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { colors } from 'utils/colors'
import { formatUpdatedAgo } from 'utils/format'
import { useLoadingCallback } from 'utils/hooks'
import { getDigitalVaultURLs } from 'utils/url'

export function DocumentCard(props: {
  document: components['schemas']['UserDocumentResponse']
  onDataChange?: () => void
  sharedWithMe?: boolean
  showType?: boolean
}) {
  const { t } = useTranslation('trusted_contact')
  const [handleShare, isSharing] = useLoadingCallback(
    async (contactShareData: ContactShareItem[]) => {
      try {
        const res = await putUserDocumentContact(props.document.uuid, {
          share:
            contactShareData?.map((contact) => ({
              trusted_contact_uuid: contact.uuid,
              access_type: 'READ_ONLY',
              share_mode: contact.share_mode || 'NOW',
            })) ?? [],
        })
        if (!res.ok) throw Error()
        toastSuccess('document_shared')
        closeMenu()
        await props.onDataChange?.()
      } catch (err) {
        handleAndToastError(err, 'failed_to_share_document')
      }
    }
  )

  const [deleteModal, showDeleteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('trusted_contact.document.delete_modal_confirm_title')}
        danger
        onConfirm={async () => {
          try {
            const res = await deleteUserDocument(props.document.uuid)
            if (!res.ok) throw Error(res?.error?.toString?.())
            toastSuccess('document_deleted')
            close()
            props.onDataChange?.()
          } catch (err) {
            handleAndToastError(err, 'failed_to_delete_doc')
          }
        }}
        close={close}
      />
    ) : null
  )

  const [inviteModal, showInviteModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactShareInvite
          hideAccessType
          defaultValue={parseSharedInvitedContacts(props.document.trusted_contacts)}
          onSubmit={(data) => {
            handleShare(data)
            close()
          }}
        />
      ) : null,
    {
      disableFocusLock: true,
      modalContentCss: { overflowY: 'visible' },
      mobilePlacement: 'top',
    }
  )

  const {
    modal: menu,
    setReferenceElement,
    open: openMenu,
    close: closeMenu,
  } = usePopperModal(
    () => (
      <>
        <Button
          type="button"
          variant="text"
          onClick={() => void showInviteModal()}
          leftIcon={<Icon name="Share" />}
        >
          {t('trusted_contact.document.popper_modal_share_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={() => void Router.push(urls.edit)}
          leftIcon={<Icon name="DocumentEdit" />}
        >
          {t('trusted_contact.document.popper_modal_edit_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          tone="danger"
          onClick={showDeleteModal}
          leftIcon={<Icon name="Bin" />}
        >
          {t('trusted_contact.document.popper_modal_delete_button')}
        </Button>
      </>
    ),
    { placement: 'bottom-end' }
  )

  const urls = getDigitalVaultURLs('document', props.document.uuid)
  const cardStatusMissingTC = useCardStatusMissingTC()
  const cardStatusComplete = useCardStatusComplete()

  return (
    <>
      {inviteModal}
      {deleteModal}
      {menu}
      <WizardGridCard
        ref={setReferenceElement}
        image={<Icon name="TextDocument" color={colors.gray[500]} />}
        entityType={props.showType ? 'Document' : null}
        data-testid={`document-item-${props.document.uuid}`}
        title={props.document?.title ?? `Document #${props.document.uuid}`}
        brief={props.document.modified ? formatUpdatedAgo(props.document.modified) : ''}
        isLoading={isSharing}
        openMenu={props.sharedWithMe ? undefined : openMenu}
        onClick={() => void Router.push(props.sharedWithMe ? urls.shared : urls.edit)}
        status={!props.document.trusted_contacts?.length ? cardStatusMissingTC : cardStatusComplete}
      />
    </>
  )
}
